import { useEffect, useState, useRef } from "react";
import { useNavigate, useParams } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Input } from "@/components/ui/input";
import { ArrowLeft, Camera, Flag, Maximize2, Minimize2, Pencil, RotateCw, Trash2, Upload, X, ZoomIn, ZoomOut, Image as ImageIcon } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import CameraCapture from "@/components/CameraCapture";
import AppHeader from "@/components/AppHeader";
import { statusBadgeStyle } from "@/lib/product-status";
import { flagFor, prettyCountry } from "@/lib/countries";
import FavoriteButton from "@/components/FavoriteButton";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const ProductDetail = () => {
  const navigate = useNavigate();
  const { barcode } = useParams();
  const [user, setUser] = useState(null);
  const [product, setProduct] = useState(null);
  const [loading, setLoading] = useState(true);
  const [review, setReview] = useState({ comment: '', cilantro_factor: '' });
  const [showCamera, setShowCamera] = useState(false);
  const [imagePreview, setImagePreview] = useState(null);
  const [uploadingImage, setUploadingImage] = useState(false);
  const [reviewToDelete, setReviewToDelete] = useState(null);
  const [imageExpanded, setImageExpanded] = useState(false);
  const [imageRotation, setImageRotation] = useState(0);
  const [imageZoom, setImageZoom] = useState(1);
  const [imagePan, setImagePan] = useState({ x: 0, y: 0 });
  const panDragRef = useRef(null);
  const [isFavorited, setIsFavorited] = useState(false);
  const [correctionOpen, setCorrectionOpen] = useState(false);
  const [correctionText, setCorrectionText] = useState('');
  const [editOpen, setEditOpen] = useState(false);
  const [editName, setEditName] = useState('');
  const [editStatus, setEditStatus] = useState('safe');
  const [editSeedOnly, setEditSeedOnly] = useState(false);
  const [editBarcode, setEditBarcode] = useState('');
  const [editLocked, setEditLocked] = useState(false);
  const [editCountries, setEditCountries] = useState([]);
  const [editCountryInput, setEditCountryInput] = useState('');
  const [editImage, setEditImage] = useState(null);
  const [reviewPhoto, setReviewPhoto] = useState(null);
  const fileInputRef = useRef(null);
  const reviewPhotoInputRef = useRef(null);
  const editImageInputRef = useRef(null);

  // Downscale to keep review photos small (server caps at ~500KB)
  const downscaleImage = (file) => new Promise((resolve, reject) => {
    const img = new window.Image();
    img.onload = () => {
      const maxDim = 1024;
      const scale = Math.min(1, maxDim / Math.max(img.width, img.height));
      const canvas = document.createElement('canvas');
      canvas.width = Math.round(img.width * scale);
      canvas.height = Math.round(img.height * scale);
      canvas.getContext('2d').drawImage(img, 0, 0, canvas.width, canvas.height);
      URL.revokeObjectURL(img.src);
      resolve(canvas.toDataURL('image/jpeg', 0.85));
    };
    img.onerror = reject;
    img.src = URL.createObjectURL(file);
  });

  const changeZoom = (direction) => {
    setImageZoom((z) => {
      const next = Math.min(4, Math.max(1, +(direction > 0 ? z * 1.4 : z / 1.4).toFixed(2)));
      if (next === 1) setImagePan({ x: 0, y: 0 });
      return next;
    });
  };

  const resetImageView = () => {
    setImageRotation(0);
    setImageZoom(1);
    setImagePan({ x: 0, y: 0 });
  };

  const startPan = (e) => {
    if (imageZoom <= 1) return;
    e.preventDefault();
    panDragRef.current = { x: e.clientX - imagePan.x, y: e.clientY - imagePan.y };
    e.currentTarget.setPointerCapture(e.pointerId);
  };

  const movePan = (e) => {
    if (!panDragRef.current) return;
    setImagePan({ x: e.clientX - panDragRef.current.x, y: e.clientY - panDragRef.current.y });
  };

  const endPan = () => { panDragRef.current = null; };

  const handleReviewPhoto = async (e) => {
    const file = e.target.files[0];
    e.target.value = '';
    if (!file) return;
    try {
      setReviewPhoto(await downscaleImage(file));
    } catch (error) {
      console.error('Photo error:', error);
      toast.error('Could not process that photo.');
    }
  };

  useEffect(() => {
    fetchData();
  }, [navigate, barcode]);

  const fetchData = async () => {
    try {
      const userResponse = await axios.get(`${API}/auth/me`);
      setUser(userResponse.data);

      const productResponse = await axios.get(`${API}/products/search?barcode=${barcode}`);
      if (productResponse.data.found) {
        const found = productResponse.data.product;
        setProduct(found);
        const favResponse = await axios.get(`${API}/me/favorites/ids`);
        setIsFavorited(favResponse.data.product_ids.includes(found.id));
      } else {
        toast.error('Product not found');
        navigate('/dashboard');
      }
    } catch (error) {
      console.error('Error fetching data:', error);
      // Only a real auth failure sends us home; transient errors keep the user
      // on the page with a message instead of bouncing to the landing screen.
      if (error?.response?.status === 401) {
        navigate('/');
      } else {
        toast.error('Could not load this product. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleUpdateStatus = async (newStatus) => {
    if (!product || product.locked_by_admin) {
      toast.error('This product is locked by admin');
      return;
    }
    try {
      await axios.patch(`${API}/products/${product.id}`, { status: newStatus });
      setProduct({ ...product, status: newStatus });
      toast.success('Product status updated!');
    } catch (error) {
      console.error('Update error:', error);
      toast.error('Failed to update product');
    }
  };

  const handleImageUpload = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (file.size > 5000000) { // 5MB limit
        toast.error('Image size should be less than 5MB');
        return;
      }
      const reader = new FileReader();
      reader.onloadend = async () => {
        setImagePreview(reader.result);
        await updateProductImage(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleCameraCapture = async (imageData) => {
    setImagePreview(imageData);
    await updateProductImage(imageData);
  };

  const updateProductImage = async (imageData) => {
    setUploadingImage(true);
    try {
      await axios.patch(`${API}/products/${product.id}`, { image_url: imageData });
      toast.success('Image added successfully!');
      await fetchData();
      setImagePreview(null);
    } catch (error) {
      console.error('Image update error:', error);
      toast.error('Failed to add image');
    } finally {
      setUploadingImage(false);
    }
  };

  const handleDeleteReview = async () => {
    if (!reviewToDelete) return;
    try {
      // Embedded reviews have no id; the (user_id, timestamp) pair — passed
      // through exactly as received — identifies one on the backend.
      await axios.delete(
        `${API}/admin/products/${product.id}/reviews` +
        `?review_user_id=${encodeURIComponent(reviewToDelete.user_id)}` +
        `&timestamp=${encodeURIComponent(reviewToDelete.timestamp)}`
      );
      toast.success('Review deleted');
      setReviewToDelete(null);
      await fetchData();
    } catch (error) {
      console.error('Delete review error:', error);
      toast.error(error.response?.data?.detail || 'Failed to delete review');
    }
  };

  const submitCorrection = async () => {
    const message = correctionText.trim();
    if (!message) {
      toast.error('Please describe what should be corrected');
      return;
    }
    try {
      await axios.post(`${API}/products/${product.id}/correction-request`, { message });
      toast.success('Thanks! An admin will take a look.');
      setCorrectionOpen(false);
      setCorrectionText('');
    } catch (error) {
      console.error('Correction request error:', error);
      toast.error('Could not send your request. Please try again.');
    }
  };

  const openEdit = () => {
    setEditName(product.name);
    setEditStatus(product.status);
    setEditSeedOnly(Boolean(product.seed_only));
    setEditBarcode(product.barcode);
    setEditLocked(Boolean(product.locked_by_admin));
    setEditCountries(product.countries || []);
    setEditCountryInput('');
    setEditImage(product.image_url || null);
    setEditOpen(true);
  };

  const addEditCountry = () => {
    const slug = editCountryInput.trim().toLowerCase().replace(/\s+/g, '-').replace(/[^a-z-]/g, '');
    if (slug && !editCountries.includes(slug)) {
      setEditCountries((prev) => [...prev, slug]);
    }
    setEditCountryInput('');
  };

  const handleEditImage = async (e) => {
    const file = e.target.files[0];
    e.target.value = '';
    if (!file) return;
    try {
      setEditImage(await downscaleImage(file));
    } catch (error) {
      console.error('Photo error:', error);
      toast.error('Could not process that photo.');
    }
  };

  const saveEdit = async () => {
    if (!editName.trim()) {
      toast.error('Name cannot be empty');
      return;
    }
    try {
      const { data } = await axios.patch(`${API}/admin/products/${product.id}`, {
        name: editName.trim(),
        barcode: editBarcode.trim(),
        status: editStatus,
        seed_only: editStatus === 'poisoned' && editSeedOnly,
        locked_by_admin: editLocked,
        countries: editCountries,
        image_url: editImage,
      });
      toast.success('Product updated');
      setEditOpen(false);
      if (data.barcode !== barcode) {
        navigate(`/product/${data.barcode}`, { replace: true });
      } else {
        await fetchData();
      }
    } catch (error) {
      console.error('Admin edit error:', error);
      toast.error(error.response?.data?.detail || 'Failed to update product');
    }
  };

  const handleAddReview = async (e) => {
    e.preventDefault();
    if (!product) return;
    try {
      await axios.post(`${API}/products/${product.id}/reviews`, { ...review, image_url: reviewPhoto });
      toast.success('Review added!');
      setReview({ comment: '', cilantro_factor: '' });
      setReviewPhoto(null);
      await fetchData();
    } catch (error) {
      console.error('Review error:', error);
      toast.error('Failed to add review');
    }
  };

  if (loading) {
    return (
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: '100vh' }}>
        <div className="loading-spinner"></div>
      </div>
    );
  }

  if (!product) {
    return null;
  }

  return (
    <div className="dashboard-container">
      {showCamera && (
        <CameraCapture
          onCapture={handleCameraCapture}
          onClose={() => setShowCamera(false)}
        />
      )}

      <AppHeader
        right={
          <Button size="sm" variant="outline" onClick={() => navigate('/dashboard')} data-testid="back-to-dashboard">
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to Dashboard
          </Button>
        }
      />

      <div className="dashboard-content" style={{ maxWidth: '900px', margin: '0 auto' }}>
        <Card data-testid="product-detail">
          <CardHeader>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start', flexWrap: 'wrap', gap: '1rem' }}>
              <div>
                <CardTitle style={{ fontSize: '2rem' }}>{product.name}</CardTitle>
                <CardDescription style={{ fontSize: '1rem', marginTop: '0.5rem' }}>Barcode: {product.barcode}</CardDescription>
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: '0.6rem' }}>
                {(() => {
                  const badge = statusBadgeStyle(product);
                  return (
                    <Badge
                      style={{
                        background: badge.background,
                        color: badge.color,
                        fontSize: '1.1rem',
                        padding: '0.75rem 1.5rem',
                        whiteSpace: 'nowrap'
                      }}
                      data-testid="product-status"
                    >
                      {badge.label}
                    </Badge>
                  );
                })()}
                <FavoriteButton
                  product={product}
                  favorited={isFavorited}
                  size="lg"
                  onChanged={({ favorited, favorites_count }) => {
                    setIsFavorited(favorited);
                    setProduct((prev) => ({ ...prev, favorites_count }));
                  }}
                />
              </div>
            </div>
          </CardHeader>
          <CardContent>
            {product.image_url ? (
              <div style={{ position: 'relative', marginBottom: '1.5rem' }}>
                <div
                  onPointerDown={startPan}
                  onPointerMove={movePan}
                  onPointerUp={endPan}
                  onPointerCancel={endPan}
                  style={imageExpanded
                    ? {
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                        background: 'hsl(var(--muted))', borderRadius: '12px', overflow: 'hidden',
                        // A square stage in every orientation: the image is
                        // scaled to fit fully, rotated or not (minWidth 0
                        // defuses the flexbox refuse-to-shrink gotcha).
                        aspectRatio: '1 / 1', width: '100%',
                        cursor: imageZoom > 1 ? 'grab' : 'default',
                        touchAction: imageZoom > 1 ? 'none' : 'auto',
                        userSelect: 'none', WebkitUserSelect: 'none',
                      }
                    : undefined}
                >
                  <img
                    src={product.image_url}
                    alt={product.name}
                    draggable={false}
                    style={imageExpanded
                      ? {
                          maxWidth: '100%', maxHeight: '100%', width: 'auto', height: 'auto',
                          minWidth: 0, objectFit: 'contain', borderRadius: '8px',
                          transform: `translate(${imagePan.x}px, ${imagePan.y}px) rotate(${imageRotation}deg) scale(${imageZoom})`,
                          transition: imageZoom === 1 ? 'transform 0.3s ease' : 'none',
                        }
                      : { width: '100%', maxHeight: '400px', objectFit: 'cover', borderRadius: '12px' }}
                    data-testid="product-image"
                  />
                </div>
                <div style={{ position: 'absolute', top: '0.75rem', right: '0.75rem', display: 'flex', gap: '0.4rem' }}>
                  {imageExpanded && (
                    <>
                      <Button
                        variant="secondary"
                        size="sm"
                        onClick={() => changeZoom(-1)}
                        disabled={imageZoom <= 1}
                        aria-label="Zoom out"
                        style={{ opacity: 0.9 }}
                        onMouseDown={(e) => e.preventDefault()}
                        data-testid="image-zoom-out"
                      >
                        <ZoomOut className="w-4 h-4" />
                      </Button>
                      <Button
                        variant="secondary"
                        size="sm"
                        onClick={() => changeZoom(1)}
                        disabled={imageZoom >= 4}
                        aria-label="Zoom in"
                        style={{ opacity: 0.9 }}
                        onMouseDown={(e) => e.preventDefault()}
                        data-testid="image-zoom-in"
                      >
                        <ZoomIn className="w-4 h-4" />
                      </Button>
                      <Button
                        variant="secondary"
                        size="sm"
                        onClick={() => setImageRotation((r) => (r + 90) % 360)}
                        aria-label="Rotate image"
                        style={{ opacity: 0.9 }}
                        onMouseDown={(e) => e.preventDefault()}
                        data-testid="image-rotate"
                      >
                        <RotateCw className="w-4 h-4" />
                      </Button>
                    </>
                  )}
                  <Button
                    variant="secondary"
                    size="sm"
                    onClick={() => {
                      setImageExpanded((v) => !v);
                      resetImageView();
                    }}
                    aria-label={imageExpanded ? 'Shrink image' : 'Show full image'}
                    style={{ opacity: 0.9 }}
                    onMouseDown={(e) => e.preventDefault()}
                        data-testid="image-zoom-toggle"
                  >
                    {imageExpanded ? <Minimize2 className="w-4 h-4" /> : <Maximize2 className="w-4 h-4" />}
                  </Button>
                </div>
              </div>
            ) : (
              <div style={{ marginBottom: '1.5rem' }}>
                <Label style={{ fontSize: '1rem', fontWeight: 600, marginBottom: '0.75rem', display: 'block' }}>
                  <ImageIcon className="w-4 h-4" style={{ display: 'inline', marginRight: '0.5rem' }} />
                  Add Product Image
                </Label>
                <div style={{ display: 'flex', gap: '0.5rem' }}>
                  <Button
                    variant="outline"
                    onClick={() => setShowCamera(true)}
                    disabled={uploadingImage}
                    style={{ flex: 1 }}
                    data-testid="add-photo-button"
                  >
                    <Camera className="w-4 h-4 mr-2" />
                    Take Photo
                  </Button>
                  <Button
                    variant="outline"
                    onClick={() => fileInputRef.current?.click()}
                    disabled={uploadingImage}
                    style={{ flex: 1 }}
                    data-testid="upload-photo-button"
                  >
                    <Upload className="w-4 h-4 mr-2" />
                    Upload Image
                  </Button>
                </div>
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/*"
                  style={{ display: 'none' }}
                  onChange={handleImageUpload}
                />
                {uploadingImage && (
                  <p style={{ marginTop: '0.5rem', color: 'hsl(var(--muted-foreground))', fontSize: '0.9rem' }}>Uploading image...</p>
                )}
              </div>
            )}

            {product.locked_by_admin ? (
              <Badge variant="outline" style={{ marginBottom: '1.5rem', fontSize: '0.95rem', padding: '0.5rem 1rem' }}>
                🔒 Status Verified and Locked by Admin
              </Badge>
            ) : (
              <div style={{ marginBottom: '1.5rem' }}>
                <Label style={{ fontSize: '1rem', fontWeight: 600 }}>Update Status:</Label>
                <div style={{ display: 'flex', gap: '0.75rem', marginTop: '0.75rem' }}>
                  <Button
                    variant="outline"
                    onClick={() => handleUpdateStatus('safe')}
                    data-testid="update-safe-button"
                    style={{ flex: 1 }}
                  >
                    Mark as Safe
                  </Button>
                  <Button
                    variant="outline"
                    onClick={() => handleUpdateStatus('poisoned')}
                    data-testid="update-poisoned-button"
                    style={{ flex: 1 }}
                  >
                    Mark as Contains Cilantro
                  </Button>
                </div>
              </div>
            )}

            <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap', marginBottom: '0.5rem' }}>
              <Button
                variant="outline"
                size="sm"
                onClick={() => setCorrectionOpen(true)}
                data-testid="request-correction-button"
              >
                <Flag className="w-4 h-4 mr-2" />
                Request correction
              </Button>
              {user?.is_admin && (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={openEdit}
                  data-testid="admin-edit-button"
                >
                  <Pencil className="w-4 h-4 mr-2" />
                  Edit product
                </Button>
              )}
            </div>

            <Dialog open={correctionOpen} onOpenChange={setCorrectionOpen}>
              <DialogContent>
                <DialogHeader>
                  <DialogTitle>Request a correction</DialogTitle>
                  <DialogDescription>
                    Wrong name, wrong status, wrong photo? Describe what should change
                    and an admin will review it.
                  </DialogDescription>
                </DialogHeader>
                <Textarea
                  value={correctionText}
                  onChange={(e) => setCorrectionText(e.target.value)}
                  placeholder={'e.g. "This product is actually Hoegaarden 0.0, not just 0.0"'}
                  rows={4}
                  maxLength={1000}
                  data-testid="correction-text"
                />
                <DialogFooter>
                  <Button variant="outline" onClick={() => setCorrectionOpen(false)}>Cancel</Button>
                  <Button onClick={submitCorrection} data-testid="submit-correction">Send request</Button>
                </DialogFooter>
              </DialogContent>
            </Dialog>

            <Dialog open={editOpen} onOpenChange={setEditOpen}>
              <DialogContent style={{ maxHeight: '85vh', overflowY: 'auto' }}>
                <DialogHeader>
                  <DialogTitle>Edit product</DialogTitle>
                  <DialogDescription>
                    Full admin edit — every field, also on verified/locked products.
                  </DialogDescription>
                </DialogHeader>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
                  <div>
                    <Label htmlFor="edit-name">Product name</Label>
                    <Input
                      id="edit-name"
                      value={editName}
                      onChange={(e) => setEditName(e.target.value)}
                      maxLength={200}
                      data-testid="edit-name-input"
                    />
                  </div>
                  <div>
                    <Label htmlFor="edit-barcode">Barcode</Label>
                    <Input
                      id="edit-barcode"
                      value={editBarcode}
                      onChange={(e) => setEditBarcode(e.target.value.replace(/\D/g, ''))}
                      maxLength={14}
                      data-testid="edit-barcode-input"
                    />
                  </div>
                  <div>
                    <Label>Status</Label>
                    <div style={{ display: 'flex', gap: '0.5rem', marginTop: '0.5rem' }}>
                      <Button
                        variant={editStatus === 'safe' ? 'default' : 'outline'}
                        onClick={() => setEditStatus('safe')}
                        style={{ flex: 1 }}
                        data-testid="edit-status-safe"
                      >
                        ✓ Safe
                      </Button>
                      <Button
                        variant={editStatus === 'poisoned' ? 'default' : 'outline'}
                        onClick={() => setEditStatus('poisoned')}
                        style={{ flex: 1 }}
                        data-testid="edit-status-poisoned"
                      >
                        ⚠ Contains Cilantro
                      </Button>
                    </div>
                  </div>
                  {editStatus === 'poisoned' && (
                    <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', cursor: 'pointer', fontSize: '0.95rem' }}>
                      <input
                        type="checkbox"
                        checked={editSeedOnly}
                        onChange={(e) => setEditSeedOnly(e.target.checked)}
                        data-testid="edit-seed-only"
                      />
                      🟠 Seeds only (no leaf — e.g. witbier, pickles)
                    </label>
                  )}
                  <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', cursor: 'pointer', fontSize: '0.95rem' }}>
                    <input
                      type="checkbox"
                      checked={editLocked}
                      onChange={(e) => setEditLocked(e.target.checked)}
                      data-testid="edit-locked"
                    />
                    🔒 Verified &amp; locked (community cannot change status)
                  </label>

                  <div>
                    <Label>Countries</Label>
                    <div style={{ display: 'flex', gap: '0.4rem', flexWrap: 'wrap', margin: '0.5rem 0' }}>
                      {editCountries.map((slug) => (
                        <Badge key={slug} variant="secondary" style={{ gap: '0.3rem' }}>
                          {flagFor(slug)} {prettyCountry(slug)}
                          <button
                            onClick={() => setEditCountries((prev) => prev.filter((c) => c !== slug))}
                            style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'inherit', display: 'flex' }}
                            aria-label={`Remove ${slug}`}
                          >
                            <X className="w-3 h-3" />
                          </button>
                        </Badge>
                      ))}
                      {editCountries.length === 0 && (
                        <span style={{ fontSize: '0.85rem', color: 'hsl(var(--muted-foreground))' }}>No countries set</span>
                      )}
                    </div>
                    <div style={{ display: 'flex', gap: '0.5rem' }}>
                      <Input
                        placeholder="e.g. belgium"
                        value={editCountryInput}
                        onChange={(e) => setEditCountryInput(e.target.value)}
                        onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); addEditCountry(); } }}
                        data-testid="edit-country-input"
                      />
                      <Button type="button" variant="outline" onClick={addEditCountry} data-testid="edit-country-add">Add</Button>
                    </div>
                  </div>

                  <div>
                    <Label>Photo</Label>
                    <div style={{ marginTop: '0.5rem' }}>
                      {editImage ? (
                        <img src={editImage} alt="Product" style={{ maxHeight: '160px', borderRadius: '8px', display: 'block', marginBottom: '0.5rem' }} data-testid="edit-image-preview" />
                      ) : (
                        <p style={{ fontSize: '0.85rem', color: 'hsl(var(--muted-foreground))', marginBottom: '0.5rem' }}>No photo</p>
                      )}
                      <div style={{ display: 'flex', gap: '0.5rem' }}>
                        <Button type="button" variant="outline" size="sm" onClick={() => editImageInputRef.current?.click()} data-testid="edit-image-browse">
                          <Upload className="w-4 h-4 mr-2" />
                          Browse…
                        </Button>
                        {editImage && (
                          <Button type="button" variant="outline" size="sm" style={{ color: 'hsl(var(--destructive))' }} onClick={() => setEditImage(null)} data-testid="edit-image-remove">
                            <Trash2 className="w-4 h-4 mr-2" />
                            Remove photo
                          </Button>
                        )}
                      </div>
                      <input ref={editImageInputRef} type="file" accept="image/*" style={{ display: 'none' }} onChange={handleEditImage} />
                    </div>
                  </div>

                  <div style={{ background: 'hsl(var(--muted))', borderRadius: '8px', padding: '0.75rem', fontSize: '0.8rem', color: 'hsl(var(--muted-foreground))', lineHeight: 1.7 }}>
                    <div><strong>id:</strong> {product.id}</div>
                    <div><strong>created by:</strong> {product.created_by}</div>
                    <div><strong>created:</strong> {product.created_at ? new Date(product.created_at).toLocaleString() : '—'}</div>
                    <div><strong>updated:</strong> {product.updated_at ? new Date(product.updated_at).toLocaleString() : '—'}</div>
                    <div><strong>favorites:</strong> {product.favorites_count || 0} · <strong>reviews:</strong> {product.reviews?.length || 0}</div>
                  </div>
                </div>
                <DialogFooter>
                  <Button variant="outline" onClick={() => setEditOpen(false)}>Cancel</Button>
                  <Button onClick={saveEdit} data-testid="save-edit">Save changes</Button>
                </DialogFooter>
              </DialogContent>
            </Dialog>

            <div style={{ marginTop: '2.5rem' }}>
              <h3 style={{ fontSize: '1.5rem', fontWeight: 600, marginBottom: '1.5rem' }}>Reviews ({product.reviews?.length || 0})</h3>
              {product.reviews && product.reviews.length > 0 ? (
                <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem', marginBottom: '2rem' }}>
                  {product.reviews.map((rev, idx) => (
                    <div key={idx} style={{ padding: '1.25rem', background: 'hsl(var(--muted))', borderRadius: '12px' }} data-testid={`review-${idx}`}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start', gap: '0.5rem' }}>
                        <div style={{ fontWeight: 600, fontSize: '1rem', marginBottom: '0.5rem', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                          {rev.user_name}
                          {user && rev.user_id === user.id && (
                            <Badge variant="secondary" style={{ fontSize: '0.7rem' }}>you</Badge>
                          )}
                        </div>
                        {user?.is_admin && (
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => setReviewToDelete(rev)}
                            aria-label="Delete review"
                            style={{ color: 'hsl(var(--destructive))' }}
                            data-testid={`delete-review-${idx}`}
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        )}
                      </div>
                      <div style={{ fontSize: '0.95rem', color: 'hsl(var(--muted-foreground))', marginBottom: '0.5rem', lineHeight: '1.5' }}>{rev.comment}</div>
                      {rev.image_url && (
                        <img
                          src={rev.image_url}
                          alt={`Review by ${rev.user_name}`}
                          style={{ maxWidth: '100%', maxHeight: '260px', borderRadius: '8px', marginBottom: '0.5rem' }}
                          data-testid={`review-photo-${idx}`}
                        />
                      )}
                      <div style={{ fontSize: '0.85rem', color: 'hsl(var(--muted-foreground))' }}>Cilantro Factor: {rev.cilantro_factor}</div>
                    </div>
                  ))}
                </div>
              ) : (
                <p style={{ color: 'hsl(var(--muted-foreground))', marginBottom: '2rem', fontSize: '1rem' }}>No reviews yet. Be the first to review!</p>
              )}

              <Dialog open={reviewToDelete !== null} onOpenChange={(open) => !open && setReviewToDelete(null)}>
                <DialogContent>
                  <DialogHeader>
                    <DialogTitle>Delete review</DialogTitle>
                    <DialogDescription>
                      Delete the review by {reviewToDelete?.user_name}? This cannot be undone.
                    </DialogDescription>
                  </DialogHeader>
                  <DialogFooter>
                    <Button variant="outline" onClick={() => setReviewToDelete(null)}>Cancel</Button>
                    <Button variant="destructive" onClick={handleDeleteReview} data-testid="confirm-delete-review">
                      Delete review
                    </Button>
                  </DialogFooter>
                </DialogContent>
              </Dialog>

              <div style={{ background: 'hsl(var(--muted))', padding: '1.5rem', borderRadius: '12px' }}>
                <h4 style={{ fontSize: '1.2rem', fontWeight: 600, marginBottom: '1rem' }}>Add Your Review</h4>
                <form onSubmit={handleAddReview} style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
                  <div>
                    <Label htmlFor="cilantro_factor">Cilantro Factor</Label>
                    <Input
                      id="cilantro_factor"
                      placeholder="e.g., Strong taste, Mild presence, None detected"
                      value={review.cilantro_factor}
                      onChange={(e) => setReview({ ...review, cilantro_factor: e.target.value })}
                      required
                      data-testid="cilantro-factor-input"
                    />
                  </div>
                  <div>
                    <Label htmlFor="comment">Your Review</Label>
                    <Textarea
                      id="comment"
                      placeholder="Share your experience with this product..."
                      value={review.comment}
                      onChange={(e) => setReview({ ...review, comment: e.target.value })}
                      required
                      rows={4}
                      data-testid="review-comment-input"
                    />
                  </div>
                  <div>
                    <Label style={{ display: 'block', marginBottom: '0.5rem' }}>Photo (optional)</Label>
                    {reviewPhoto ? (
                      <div style={{ position: 'relative', display: 'inline-block' }}>
                        <img src={reviewPhoto} alt="Review" style={{ maxHeight: '140px', borderRadius: '8px' }} data-testid="review-photo-preview" />
                        <Button
                          type="button"
                          variant="secondary"
                          size="sm"
                          onClick={() => setReviewPhoto(null)}
                          style={{ position: 'absolute', top: '0.35rem', right: '0.35rem', opacity: 0.9 }}
                          aria-label="Remove photo"
                        >
                          <X className="w-4 h-4" />
                        </Button>
                      </div>
                    ) : (
                      <Button
                        type="button"
                        variant="outline"
                        onClick={() => reviewPhotoInputRef.current?.click()}
                        data-testid="add-review-photo"
                      >
                        <Camera className="w-4 h-4 mr-2" />
                        Add a photo
                      </Button>
                    )}
                    <input
                      ref={reviewPhotoInputRef}
                      type="file"
                      accept="image/*"
                      capture="environment"
                      style={{ display: 'none' }}
                      onChange={handleReviewPhoto}
                    />
                  </div>
                  <Button type="submit" size="lg" data-testid="submit-review-button">Submit Review</Button>
                </form>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default ProductDetail;